import { useEffect } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import { Paper, Divider } from '@mui/material';

import { usePathname } from 'src/routes/hooks';

import { useResponsive } from 'src/hooks/use-responsive';

import { useAuthContext } from 'src/auth/hooks';
import { NAV } from 'src/layouts/config-layout';
import { useEntityContext } from 'src/entity/hooks';
import NavGlobal from 'src/layouts/dashboard/nav-global';

import Scrollbar from 'src/components/scrollbar';
import { NavSectionVertical } from 'src/components/nav-section';
import MyEntitiesMenu from 'src/components/myentities-menu/myentities-menu';

// ----------------------------------------------------------------------

export default function NavVertical({ openNav, onCloseNav, navData }) {
  const { user } = useAuthContext();
  const pathname = usePathname();
  const lgUp = useResponsive('up', 'lg');

  const {
    entity: { name, logo },
  } = useEntityContext();

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          m: 2,
        }}
      >
        <MyEntitiesMenu
          size="small"
          sx={{
            textAlign: 'left',
            borderRadius: 1,
            fontSize: 11,
            cursor: 'pointer',
            typography: 'overline',
            display: 'block',
            color: 'text.disabled',
            width: '100%',
            transition: (theme) =>
              theme.transitions.create(['color'], {
                duration: theme.transitions.duration.shortest,
              }),
            '&:hover': {
              color: 'primary.contrastText',
            },
          }}
        >
          <Paper
            sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            variant="outlined"
          >
            <Box
              component="img"
              src={logo}
              alt={name}
              sx={{ width: '100%', height: 75, objectFit: 'contain' }}
            />
          </Paper>
        </MyEntitiesMenu>
      </Box>

      <NavSectionVertical
        data={navData}
        slotProps={{
          currentRole: user?.role,
        }}
      />

      <Box sx={{ flexGrow: 1 }} />

      <Divider sx={{ mt: 2 }} />

      <NavGlobal />
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_VERTICAL },
      }}
    >
      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.W_VERTICAL,
            borderRight: (theme) => `solid 1px ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: (theme) => theme.shadows[6],
            zIndex: (theme) => theme.zIndex.appBar + 1,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.W_VERTICAL,
            },
          }}
          sx={{
            '& .MuiDrawer-paper': {
              bgcolor: 'background.paper',
              color: 'text.primary',
              borderRight: (theme) => `solid 1px ${theme.palette.divider}`,
              boxShadow: (theme) => theme.shadows[6],
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}

NavVertical.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
  navData: PropTypes.array,
};
